// Pure copy resolver for the material delete / restore confirm modal.
// Lifted out of the orchestrator so the modal title + body + button
// label rendering is testable without rendering the orchestrator and
// so `ManufacturingInventoryPanel.tsx` stays under the file-size cap.
// Returns null when nothing is pending so the caller can use it
// directly inside JSX.
//
// Dev-sync 184bc79f.

import type { ReactNode } from "react";
import type { MaterialDeletionPending } from "./useMaterialDeletionFlow";

export type DeletionConfirmCopy = {
  /** Modal variant — drives primary button color (amber for delete,
   *  emerald for restore). */
  variant: "delete" | "restore";
  title: string;
  /** ReactNode so the orchestrator can render `<p>` + `<strong>`
   *  emphasis without escaping back through string templating. */
  body: ReactNode;
  confirmLabel: string;
};

/** Returns the modal copy for the current pending request, or null
 *  when nothing is open. Pure — every input is a primitive / array
 *  + the pending shape from the deletion-flow hook. */
export function describeDeletionPending(
  pending: MaterialDeletionPending | null
): DeletionConfirmCopy | null {
  if (!pending) return null;
  switch (pending.kind) {
    case "single-delete":
      return {
        variant: "delete",
        title: `Delete ${pending.material.name}?`,
        body: (
          <span>
            The material is moved to <strong>Inactive</strong> and hidden from
            new receipts + recipes. You can restore it from the materials list
            anytime — see{" "}
            <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px] dark:bg-zinc-800">
              docs/planning/mfg_material_delete_with_restore.md
            </code>{" "}
            for the retention policy.
          </span>
        ),
        confirmLabel: "Delete material",
      };
    case "single-restore":
      return {
        variant: "restore",
        title: `Restore ${pending.material.name}?`,
        body: (
          <span>
            Flips the material back to <strong>Active</strong> so it reappears
            in receipts + recipe pickers. Existing changelog entries on the
            material are unchanged.
          </span>
        ),
        confirmLabel: "Restore material",
      };
    case "bulk-delete": {
      const n = pending.materialIds.length;
      return {
        variant: "delete",
        title: `Delete ${n} material${n === 1 ? "" : "s"}?`,
        body: (
          <span>
            The selection will be flipped to <strong>Inactive</strong> in one
            pass. Each row stays restorable individually from the materials
            list. Failures are reported per-row in the toast — partial
            successes are kept.
          </span>
        ),
        confirmLabel: `Delete ${n} selected`,
      };
    }
    case "bulk-restore": {
      const n = pending.materialIds.length;
      return {
        variant: "restore",
        title: `Restore ${n} material${n === 1 ? "" : "s"}?`,
        body: (
          <span>
            The selection will be flipped back to <strong>Active</strong>{" "}
            in one pass. Failures are reported per-row in the toast — partial
            successes are kept.
          </span>
        ),
        confirmLabel: `Restore ${n} selected`,
      };
    }
    default:
      return null;
  }
}
